import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../../context/LanguageContext';
import './header.scss';
import logo from '../../assets/logo.jpg';

const NAV_KEYS = [
  { to: '/', key: 'home' },
  { to: '/about', key: 'about' },
  { to: '/courses', key: 'courses' },
  { to: '/consultancy', key: 'consultancy' },
  { to: '/careers', key: 'careers' },
  { to: '/contact', key: 'contact' },
];

const NAV_PANEL_ID = 'site-primary-nav';
const MOBILE_NAV_MQ = '(max-width: 992px)';

const isNavActive = (pathname, to) => {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
};

const BR_TAG = /<br\s*\/?>/gi;

const TextWithBreaks = ({ text }) => {
  const parts = String(text).split(BR_TAG);
  if (parts.length <= 1) return text;

  return parts.map((part, index) => (
    <React.Fragment key={index}>
      {index > 0 ? <br /> : null}
      {part}
    </React.Fragment>
  ));
};

const Header = () => {
  const { lang, setLang, content } = useTranslation();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [narrowNav, setNarrowNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_NAV_MQ).matches
  );

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleMenu = useCallback(() => setMenuOpen((o) => !o), []);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_NAV_MQ);
    const syncLayout = () => {
      const next = mq.matches;
      setNarrowNav(next);
      if (!next) setMenuOpen(false);
    };

    syncLayout();
    mq.addEventListener('change', syncLayout);
    return () => mq.removeEventListener('change', syncLayout);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };

    document.body.classList.add('site-header--menu-open');
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.classList.remove('site-header--menu-open');
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, closeMenu]);

  return (
    <header className="section-full site-header">
      <div className="container">
        <div className="site-header__row">
          <Link
            to="/"
            className="site-brand"
            aria-label={content.header.brandAria}
            onClick={() => {
              if (pathname === '/') window.scrollTo(0, 0);
            }}
          >
            <img src={logo} alt="HumPro logo" className="site-brand__logo" />
            <span className="site-brand__tagline">
              <TextWithBreaks text={content.common.brandAnchor} />
            </span>
          </Link>

          <button
            type="button"
            className="site-header__menu-btn"
            aria-expanded={menuOpen}
            aria-controls={NAV_PANEL_ID}
            aria-label={menuOpen ? content.header.menuClose : content.header.menuOpen}
            onClick={toggleMenu}
          >
            <span className="site-header__menu-icon" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>

          <div
            className="site-header__backdrop"
            aria-hidden={!menuOpen}
            onClick={closeMenu}
          />

          <div
            className="site-header__panel"
            id={NAV_PANEL_ID}
            aria-hidden={narrowNav && !menuOpen ? true : undefined}
          >
            <nav className="site-nav" aria-label="Main navigation">
              {NAV_KEYS.map((item) => {
                const active = isNavActive(pathname, item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`site-nav__link ${active ? 'is-active' : ''}`.trim()}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => {
                      closeMenu();
                      if (item.to === '/' && pathname === '/') {
                        window.scrollTo(0, 0);
                      }
                    }}
                  >
                    {content.header.nav[item.key]}
                  </Link>
                );
              })}
            </nav>

            <div
              className="lang-switch"
              role="group"
              aria-label={content.header.langGroupAria}
            >
              <button
                type="button"
                className={`lang-switch__btn ${lang === 'en' ? 'is-active' : ''}`}
                aria-pressed={lang === 'en'}
                onClick={() => setLang('en')}
              >
                <span aria-hidden="true">EN</span>
                <span className="visually-hidden">{content.header.langEnHidden}</span>
              </button>
              <button
                type="button"
                className={`lang-switch__btn ${lang === 'hi' ? 'is-active' : ''}`}
                aria-pressed={lang === 'hi'}
                title={content.header.langHiTitle}
                onClick={() => setLang('hi')}
              >
                <span aria-hidden="true">HI</span>
                <span className="visually-hidden">{content.header.langHiHidden}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
